import React, { useRef, useState, useCallback, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { DatePicker, Typography } from 'antd';
import './style.css';
import moment from 'moment';
import { ApiCaller } from '../../../config';
import { Loader, ProfileImage } from '../../../components';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Custom tooltip with scrollable content
const CustomTooltip = ({ data, label, position, onMouseEnter, onMouseLeave }) => {
    const responses = data?.responses || [];

    return (
        <div
            className="floating-tooltip-portal"
            style={{
                left: position.x,
                top: position.y,
                position: "absolute",
                zIndex: 1000,
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="tooltip-container">
                <div className="tooltip-title">{label} ({responses.length} responses)</div>

                <div className="tooltip-content">
                    {responses.length > 0 ? (
                        responses.map((res, i) => (
                            <div key={i} className="tooltip-item">
                                <ProfileImage
                                    className="tooltip-image"
                                    src={res.serviceProvider?.imageThumbUrl}
                                    alt={res.serviceProvider?.businessName || "provider"}
                                />
                                <div className="tooltip-text">
                                    <div className="tooltip-business">
                                        {res.serviceProvider?.businessName || 'Unknown Business'}
                                    </div>
                                    <div className="tooltip-time">
                                        {moment(res.responseTime).format("DD MMM, hh:mm A")}
                                    </div>
                                    <div className="tooltip-status">
                                        Status: {res.status}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="tooltip-empty">No responses for this day</div>
                    )}
                </div>
            </div>
        </div>
    );
};

const WeeklyResponseRateChart = ({ id }) => {

    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [tooltipState, setTooltipState] = useState({
        visible: false,
        data: null,
        label: "",
        position: { x: 0, y: 0 },
        isHoveringTooltip: false,
    });
    const [value, setValue] = useState([moment().subtract(6, "day"), moment()]);
    const containerRef = useRef(null);
    const hideTimeoutRef = useRef(null);
    const [refresh, setRefresh] = useState(false);

    useEffect(() => {
        if (id) {
            (
                async () => {
                    try {
                        setIsLoading(true)
                        let res = await ApiCaller.Get(`/admin/service-user/${id}/response-rate?startDate=${value?.length > 0 ? value[0].format('YYYY-MM-DD') : ""}&endDate=${value?.length > 1 ? value[1].format('YYYY-MM-DD') : ""}`);
                        if (res?.data && res?.data?.data?.data?.some(resp => resp?.responses?.length > 0)) {
                            setData(res?.data?.data?.data)
                        }
                        else {
                            setData([])
                        }
                        console.log(res?.data?.data?.data);
                    }
                    catch (error) {
                        console.log(error)
                    }
                    finally {
                        setIsLoading(false)
                    }
                }
            )()
        }
    }, [id, refresh])


    // Transform the data
    const chartData = data?.length > 0 ? data.map((dayData) => ({
        day: dayData.day,
        responsesCount: dayData.responses.length,
        responses: dayData.responses,
    })) : [];

    const clearHideTimeout = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };

    const scheduleHide = () => {
        clearHideTimeout();
        hideTimeoutRef.current = setTimeout(() => {
            if (!tooltipState.isHoveringTooltip) {
                setTooltipState(prev => ({ ...prev, visible: false }));
            }
        }, 100);
    };

    const handleBarMouseEnter = useCallback((data, event) => {
        clearHideTimeout();

        const container = containerRef.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const mouseX = event.clientX - containerRect.left;
        const mouseY = event.clientY - containerRect.top;

        // Position tooltip to the right of the cursor, but ensure it stays within bounds
        let tooltipX = mouseX + 20;
        let tooltipY = mouseY - 50;

        // Adjust if tooltip would go outside container
        const tooltipWidth = 300; // approximate width
        const tooltipHeight = 220; // approximate height

        if (tooltipX + tooltipWidth > containerRect.width) {
            tooltipX = mouseX - tooltipWidth - 20;
        }

        if (tooltipY < 0) {
            tooltipY = 10;
        }

        if (tooltipY + tooltipHeight > containerRect.height) {
            tooltipY = containerRect.height - tooltipHeight - 10;
        }

        setTooltipState({
            visible: true,
            data: data,
            label: data.day,
            position: { x: tooltipX, y: tooltipY },
            isHoveringTooltip: false,
        });
    }, []);

    const handleBarMouseLeave = useCallback(() => {
        scheduleHide();
    }, []);

    const handleTooltipMouseEnter = useCallback(() => {
        clearHideTimeout();
        setTooltipState(prev => ({ ...prev, isHoveringTooltip: true }));
    }, []);

    const handleTooltipMouseLeave = useCallback(() => {
        setTooltipState(prev => ({ ...prev, isHoveringTooltip: false, visible: false }));
    }, []);

    const handleContainerMouseLeave = useCallback(() => {
        scheduleHide();
    }, []);


    // Updated handleChange function with smooth 7-day selection
    const handleChange = (dates) => {
        if (!dates || dates.length === 0) {
            setValue([]);
            setRefresh(!refresh);
            return;
        }

        // When user selects first date (onCalendarChange will handle this)
        // This function will only be called when the picker closes with final selection
        if (dates.length === 2 && dates[0] && dates[1]) {
            const startDate = dates[0];
            const endDate = dates[1];
            const daysDiff = endDate.diff(startDate, 'days');

            // Always ensure exactly 7 days (6 day difference)
            if (daysDiff !== 6) {
                const adjustedEndDate = startDate.clone().add(6, "days");
                setValue([startDate, adjustedEndDate]);
            } else {
                setValue([startDate, endDate]);
            }
            setRefresh(!refresh);
        }
    };

    // New function to handle calendar changes (when user clicks dates)
    const handleCalendarChange = (dates) => {
        if (!dates || dates.length === 0) {
            return;
        }

        // When user selects the first date, automatically set the end date
        if (dates.length === 1 && dates[0]) {
            const startDate = dates[0];
            const endDate = startDate.clone().add(6, "days");

            // Set the value immediately for smooth UX
            setValue([startDate, endDate]);

            // Optional: Show a message to user
            // message.success(`Week selected: ${startDate.format('DD/MM/YYYY')} to ${endDate.format('DD/MM/YYYY')}`);
        }
    };


    return (
        <div className="response-rate-container">
            {
                isLoading ?
                    <div className="user-table-loader-container">
                        <Loader.Circular />
                    </div>
                    :
                    <>
                        <div className="response-rate-filter">
                            <div className="custom-representation-bar-container">
                                <div className="bar-container">
                                    <div className="bar-1"></div>
                                    <small>on hover (responses from user to expert - listing)</small>
                                </div>
                            </div>
                            <RangePicker
                                value={value}
                                onChange={handleChange}
                                onCalendarChange={handleCalendarChange}
                                format="DD/MM/YYYY"
                                allowClear
                                placeholder={["Start Date", "End Date"]}
                                renderExtraFooter={() => (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '8px',
                                        color: '#666',
                                        fontSize: '12px',
                                        borderTop: '1px solid #f0f0f0'
                                    }}>
                                        📅 Select any start date to automatically choose a 7-day period
                                    </div>
                                )}
                            />
                        </div>
                        {
                            data.length > 0
                                ?
                                <div
                                    ref={containerRef}
                                    className="response-rate-chart-wrapper"
                                    onMouseLeave={handleContainerMouseLeave}
                                    style={{ position: 'relative' }}
                                >
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart
                                            data={chartData}
                                            margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="day" />
                                            <YAxis allowDecimals={false} />
                                            <Bar
                                                dataKey="responsesCount"
                                                fill="#FFB800"
                                                radius={[6, 6, 0, 0]}
                                                barSize={40}
                                                onMouseEnter={(data, index, event) => {
                                                    handleBarMouseEnter(data, event);
                                                }}
                                                onMouseLeave={handleBarMouseLeave}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>

                                    {tooltipState.visible && tooltipState.data && (
                                        <CustomTooltip
                                            data={tooltipState.data}
                                            label={tooltipState.label}
                                            position={tooltipState.position}
                                            onMouseEnter={handleTooltipMouseEnter}
                                            onMouseLeave={handleTooltipMouseLeave}
                                        />
                                    )}
                                </div>
                                :
                                <p style={{ textAlign: 'center' }}>No data Available</p>
                        }
                    </>
            }
        </div>
    );
};

export default WeeklyResponseRateChart;